<template>
  <div class="padding">
    <!-- filter header -->
    <div class="row m-t-sm">
      <div class="col-md-3">
        <h5 class="_300 margin m-b-md">Products</h5>
      </div>
  
      <div class="col-md-9">
        <div class="row">
          <div 
            :class="{'col-md-offset-6': isForMultiLayerCategory}"
            class="col-md-3 p-b-sm">
            <select
              v-model="productType"
              class="form-control c-select">
              <option :value="null">Product Type</option>
              <option
                v-for="type in productTypeList"
                :value="type.id"
                :key="type.id">
                {{ type.name }}
              </option>
            </select>
          </div>
  
          <div
            v-show="isRequiredCategoryFilter"
            class="col-md-3 p-b-sm">
            <select
              v-model="category"
              class="form-control c-select">
              <option :value="null">{{ trans('product.select_category') }}</option>
              <option
                v-for="category in categories"
                :value="category.id"
                :key="category.id">
                {{ category.name }}
              </option>
            </select>
          </div>
  
          <div 
            v-show="isRequiredTagFilter"
            class="col-md-3 p-b-sm">
            <select
              v-model="tagSelected"
              class="form-control c-select">
              <option value="0">{{ trans('product.select_tag') }}</option>
              <option
                v-for="tag in tagList"
                :value="tag.id"
                :key="tag.id">
                {{ tag.name }}
              </option>
            </select>
          </div>
  
          <div class="col-md-3 p-b-sm">
            <outlet-selector 
              :outlet="$store.state.report.activeOutlet" 
              :ignore-first-outlet="true" />
          </div>
        </div>
      </div>
    </div>
  
    <div class="box">
      <v-server-table
        ref="datatable"
        :url="api"
        :name="tableName"
        :columns="columns"
        :options="options">
  
        <template
          slot="code"
          slot-scope="props">
          <optional-cell-value
            v-model="props.row.code"/>
        </template>
  
        <template
          slot="category"
          slot-scope="props">
          <optional-cell-value
            v-model="props.row.category"
            :empty-text="`${trans('product.no_category_assigned')}`"/>
        </template>
  
      </v-server-table>
  
    </div>
  
    <vuetableitem_form 
      ref="itemForm" 
      :tag-selected="tagSelected" 
      @product-saved="refresh"/>
    <import-item/>
  
    <item-bulk-modal
      ref="itemBulkModal" />
  </div>
  
</template>
  
  <script>
import { Event } from '../../../event-bus';
import Auth from '@js/plugins/auth';
import importItem from './import-item.vue';
import ItemBulkModal from './item-bulk-modal';
import { mapGetters, mapActions } from 'vuex';
import OptionalCellValue from '@js/components/optional-cell-value';
import formattedCurrencyMixin from '@js/mixins/formatted-currency';

const TableName = 'VUETABLE_ITEM';

export default {
    components: {
        importItem,
        ItemBulkModal,
        OptionalCellValue,
    },

    mixins: [formattedCurrencyMixin],

    props: {
        isRequiredTagFilter: {
            type: Boolean,
            default: true,
        },
        isRequiredCategoryFilter: {
            type: Boolean,
            default: true,
        },
        tagSelected: {
            type: Number,
            default: 0,
        },
        isForMultiLayerCategory: {
            type: Boolean,
            default: false,
        },
    },

    data: function() {
        var that = this;
        let canManageItems = Auth.hasAnyPermission(
            'franchise_manage_items',
            'franchise_manage_products',
        );

        let actionButtons = [
            {
                title: 'Create Product',
                action: () => {
                    this.show_popup(null);
                },
            },
            {
                title: 'Import',
                action: () => {
                    this.showImportForm();
                },
            },
        ];

        if (!canManageItems || this.isForMultiLayerCategory) {
            actionButtons = [];
        }

        return {
            product: {},
            tableName: TableName,
            // tagSelected: 0,
            category: null,
            productType: null,
            api: Laroute.route('franchise.inventory.item.index'),
            columns: [
                'code',
                'ronpos_product_code',
                'name',
                'outlet',
                'category',
                'tags',
                'price_type',
                'cost',
                'price',
            ],
            options: {
                preserveState: true,
                columnsDropdown: true,
                checkbox: canManageItems,
                checkboxActions: [
                    {
                        title: 'Edit Products',
                        callback: () => {
                            this.$refs.itemBulkModal.showModal();
                        },
                    },
                ],
                checkbox_actions: {
                    api_delete: Laroute.route(
                        'franchise.inventory.item.delete',
                    ),
                    delete_title: 'Delete Products',
                },
                customFilters: ['outlet_id', 'tag_id', 'category_id'],
                initFilters: { outlet_id: that.$store.getters.activeOutlet },
                orderBy: { column: 'name', ascending: 1 },
                headings: {
                    price_type: 'Price Mode',
                    outlet: this.trans('general.outlet'),
                    tags: this.trans('product.tags_title'),
                    category: this.trans('category.title'),
                    code: this.trans('product.code'),
                    name: this.trans('product.name'),
                    // cost: this.trans('product.cost'),
                    // price: this.trans('product.unit_price'),
                    cost: `Cost (${this.getCurrency()})`,
                    price: `Price (${this.getCurrency()})`,
                    ronpos_product_code: 'External Code',
                },
                columnsClasses: {
                    cost: 'text-right',
                    price: 'text-right',
                },
                templates: {
                    tags: (h, row) => {
                        return require('./vuetableitem_tags.jsx').call(
                            this,
                            h,
                            row,
                        );
                    },
                    outlet: (h, row) => {
                        return require('./vuetableitem_outlet.jsx').call(
                            this,
                            h,
                            row,
                        );
                    },
                    price_type(h, row) {
                        if (row.price_type == 0) {
                            return 'Fixed';
                        }

                        if (row.price_type == 1) {
                            return 'Unit';
                        }

                        if (row.price_type == 2) {
                            return 'Variable';
                        }
                    },
                    ronpos_product_code(h, row) {
                        return row.ronpos_product_code != null
                            ? row.ronpos_product_code
                            : '';
                    },
                },
                csv_url: this.exportType('CSV'),
                xls_url: this.exportType('XLSX'),
                actionButtons: actionButtons,

                rowClassCallback: row => {
                    let clazz = ['pointer'];

                    if (row.status == 0) {
                        clazz.push('text-muted');
                    }

                    return clazz;
                },

                rowClickCallback: that.show_popup,
                importCallback: that.showImportForm,
            },
        };
    },

    computed: {
        activeOutlet() {
            return this.$store.getters.activeOutlet;
        },

        searchQuery() {
            return this.$refs.datatable.query;
        },

        productTypeList() {
            return [{ id: 0, name: 'HQ' }, { id: 1, name: "Dealers Choice's" }];
        },

        ...mapGetters('category', ['categories']),

        ...mapGetters('tag', ['tagList']),
    },

    watch: {
        activeOutlet: function() {
            this.$store.commit(`${TableName}/SET_CUSTOM_FILTER`, {
                filter: 'outlet_id',
                value: this.activeOutlet,
            });
        },

        tagSelected(ov, nv) {
            this.$store.commit(`${TableName}/SET_CUSTOM_FILTER`, {
                filter: 'tag_id',
                value: this.tagSelected,
            });
        },

        category() {
            this.$store.commit(`${TableName}/SET_CUSTOM_FILTER`, {
                filter: 'category_id',
                value: this.category,
            });
        },

        productType() {
            this.$store.commit(`${TableName}/SET_CUSTOM_FILTER`, {
                filter: 'product_type',
                value: this.productType,
            });
        },

        '$store.getters.currency'() {
            this.options.headings.cost = `Cost (${this.getCurrency()})`;
            this.options.headings.price = `Price (${this.getCurrency()})`;
        },
    },

    created() {
        this.fetchAllTags();

        this.registerEvent();
    },

    mounted() {
        Event.$on('import-completed', () => {
            this.refresh();
            this.fetchAllTags();
        });

        Event.$on('import-error', () => {
            this.refresh();
            this.fetchAllTags();
        });

        Event.$on('bulk-product-updated', () => {
            this.$refs.datatable.clearCheckboxSelected();
            this.refresh();
        });

        this.filterTagSelected();
    },

    beforeDestroy() {
        this.$bus.$off('table_item::reload');
    },

    methods: {
        ...mapActions('tag', ['getTagList']),

        fetchAllTags() {
            this.getTagList({
                limit: null,
                maxprice: null,
                modifiers: 1,
            });
        },

        show_popup(row) {
            this.$refs.itemForm.$emit('show-form', row);
        },

        refresh() {
            this.$refs.datatable.getData();
        },

        showImportForm() {
            $('#import-item').modal('show');
        },

        filterTagSelected() {
            if (this.tagSelected != 0) {
                this.$store.commit(`${TableName}/SET_CUSTOM_FILTER`, {
                    filter: 'tag_id',
                    value: this.tagSelected,
                });
            }
        },

        registerEvent() {
            this.$bus.$on('table_item::reload', () => {
                this.refresh();
            });

            this.$bus.$on('table_item::show_create_form', () => {
                this.show_popup(null);
            });
        },

        exportType(type) {
            if (this.isForMultiLayerCategory) return false;

            return Laroute.route('franchise.inventory.item.exportAsFile', {
                type: type,
            });
        },
    },
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/inventory/item/vuetable_item.vue